import React, {useEffect, useState} from "react"
import {Button, Drawer, Input, Layout, message, Select, Space, Table, Tag} from "antd"
import {DownloadOutlined} from "@ant-design/icons"
import type {ColumnsType} from "antd/es/table"
import {useDispatch, useSelector} from "react-redux"
import ServiceOrdersInfo from "./ServiceOrdersInfo"
import global from "../../global"

const {Search} = Input

/**
 * 定义ServiceOrderType
 * @constructor
 */
interface ServiceOrderType {
  nickName: string
  avatar: string
  orderNo: string
  orderType: string
  price: number
  description: string
  name: string
  idCard: string
  mobile: string
  serviceName: string
  programme: string
  amount: number
  payStatus: string
  orderStatus: string
  remark: string
}

const ServiceOrdersTable: React.FC = () => {
  const [infoOpen, setInfoOpen] = useState(false)
  const [currentOrderNo, setCurrentOrderNo] = useState("")

  const serviceOrders = useSelector((state: any) => state.serviceOrders)
  const dispatch = useDispatch()

  const [messageApi, contextMessageHolder] = message.useMessage()

  const columns: ColumnsType<ServiceOrderType> = [
    {
      title: "订单号",
      dataIndex: "orderNo",
      key: "orderNo",
    },
    {
      title: "用户姓名",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "手机号码",
      dataIndex: "mobile",
      key: "mobile",
    },
    {
      title: "服务类型",
      dataIndex: "serviceName",
      key: "serviceName",
    },
    {
      title: "价格",
      dataIndex: "price",
      key: "price",
      render: (text: number) => `¥${text/100}`
    },
    {
      title: "支付状态",
      dataIndex: "payStatus",
      key: "payStatus",
      render: (text: string) => {
        const color = global.getPayStatusColor(text)
        const statusText = global.getPayStatusText(text)
        return <Tag color={color}>{statusText}</Tag>
      }
    },
    {
      title: "订单状态",
      dataIndex: "orderStatus",
      key: "orderStatus",
      render: (text: string) => {
        const color = global.getOrderStatusColor(text)
        const statusText = global.getOrderStatusText(text)
        return <Tag color={color}>{statusText}</Tag>
      }
    },
    {
      title: "操作",
      key: "action",
      render: (_, record) => {
        return (
          <>
            <Button type="link" size={"small"} onClick={() => handleInfo(record)}>
              查看详情
            </Button>
          </>
        )
      },
    },
  ]

  const onSearch = (value: string) => {
    let request = serviceOrders.request
    request.keywords = value
    request.pageNum = 1
    dispatch.serviceOrders.setData({request})
  }

  const handleInfo = (record: ServiceOrderType) => {
    setCurrentOrderNo(record.orderNo)
    setInfoOpen(true)
  }

  const onInfoClose = () => {
    setInfoOpen(false)
    setCurrentOrderNo("")
  }

  const setService = (serviceId: string) => {
    let request = serviceOrders.request
    request.serviceId = serviceId
    request.pageNum = 1
    dispatch.serviceOrders.setData({request})
  }

  const setPayStatus = (payStatus: string) => {
    let request = serviceOrders.request
    request.payStatus = payStatus
    request.pageNum = 1
    dispatch.serviceOrders.setData({request})
  }

  const handleDownload = async () => {
    try {
      await dispatch.serviceOrders.download()
      messageApi.success("下载成功")
    } catch (error) {
      console.error('Download error:', error)
      messageApi.error("下载失败")
    }
  }

  const serviceOptions = [
    {label: "全部", value: ""},
    ...serviceOrders.services.map((service: any) => ({
      label: service.name,
      value: service.id
    }))
  ]

  useEffect(() => {
    dispatch.serviceOrders.getServices()
  }, [dispatch.serviceOrders])

  useEffect(() => {
    dispatch.serviceOrders.getPage()
  }, [serviceOrders.request.keywords, serviceOrders.request.pageNum, serviceOrders.request.serviceId, serviceOrders.request.payStatus, dispatch.serviceOrders])

  return (
    <>
      {contextMessageHolder}
      <Layout>
        <Space>
          <Select style={{width: 350}} onChange={(serviceId: string) => setService(serviceId)} options={serviceOptions} placeholder="选择服务"/>
          <Select style={{width: 120}} onChange={(payStatus: string) => setPayStatus(payStatus)} options={global.getPayStatusOptions()} placeholder="付款状态"/>
          <Search placeholder="输入查询内容" enterButton="查询" onSearch={onSearch}/>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
            下载
          </Button>
        </Space>
        <Layout style={{marginTop: "10px"}}>
          <Table
            columns={columns}
            dataSource={serviceOrders.data.list}
            pagination={{
              pageSize: serviceOrders.data.pageSize,
              total: serviceOrders.data.total,
              onChange: (page, pageSize) => {
                let request = {...serviceOrders.request, pageNum: page, pageSize}
                dispatch.serviceOrders.setData({request})
              },
            }}
          />
        </Layout>
      </Layout>

      <Drawer
        title="服务订单详情"
        width={600}
        open={infoOpen}
        onClose={onInfoClose}
        styles={{body: {paddingBottom: 80}}}
      >
        {currentOrderNo && (
          <ServiceOrdersInfo 
            orderNo={currentOrderNo} 
            onClose={onInfoClose}
            onRefundSuccess={() => {
              messageApi.success("退款成功")
              dispatch.serviceOrders.getPage()
            }}
          />
        )}
      </Drawer>
    </>
  )
}

export default ServiceOrdersTable 